






































































































































import moment from 'moment';

import {
  EmployeeType,
  IEmployee,
  ITimeOffRequest,
  ITimeOffRequestData,
  IUserData,
  TimeOffRequest,
  TimeOffStateEnum,
  TimeOffUnitEnum
} from '@/models';
import { ITimeOffRequestSearchParams } from '@/services/timeoff-request';

import Vue, { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { ErrorManager } from '@/models/error';

import ErrorAlert from '../common/ErrorAlert.vue';
import TimeOffRequestDetailMenuCard from './TimeOffRequestDetailMenuCard.vue';
import TimeOffRequestActivityDialog from './TimeOffRequestActivityDialog.vue';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';

import mixins from '@/utils/mixins';
import Functions from '@/mixins/functions';
import auth from '@/mixins/auth';

import { IFetchTimeOffRequestPayload } from '@/services/timeoff-request';

const baseMixins = mixins(Functions, auth);

interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    files: HTMLInputElement;

    timeOffRequestActivityDialog: InstanceType<
      typeof TimeOffRequestActivityDialog
    >;
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    name: 'TimeOffList',

    components: {
      ConfirmationDialog,
      ErrorAlert,
      TimeOffRequestDetailMenuCard,
      TimeOffRequestActivityDialog
    },

    props: {
      externalErrorMessageDetail: String,
      externalErrorMessage: String,
      isLoading: Boolean,
      showRequestApprovalList: Boolean,
      params: Object as PropType<ITimeOffRequestSearchParams>
    },

    data() {
      return {
        EmployeeType,

        errorMessageDetail: this.externalErrorMessageDetail,
        errorMessage: this.externalErrorMessage,

        loading: this.isLoading,

        page: 1,

        selectedTimeOff: {} as ITimeOffRequestData,
        dialog: false,

        headers: [
          {
            text: 'Employee',
            sortable: false,
            value: 'employee',
            width: 250
          },
          {
            text: 'Type',
            sortable: false,
            value: 'type.name',
            width: 180
          },
          {
            text: 'Year',
            sortable: false,
            value: 'entitlement_year',
            width: 100
          },
          { text: 'Start Date', sortable: false, value: 'start', width: 150 },
          { text: 'End Date', sortable: false, value: 'end', width: 150 },
          {
            text: 'Duration',
            sortable: false,
            value: 'amount_requested',
            width: 150
          },
          {
            text: 'Status',
            sortable: false,
            value: 'state',
            align: 'center',
            width: 200
          },
          {
            text: 'Last Modified',
            sortable: false,
            value: 'updated_at',
            width: 200
          },
          {
            text: '',
            sortable: false,
            align: 'center',
            value: 'actions',
            width: 120
          }
        ],

        TimeOffStateEnum,
        cancelConfirmationDialog: false,

        state: TimeOffStateEnum.Draft
      };
    },
    computed: {
      ...mapGetters({
        timeOffRequests: 'timeOffRequest/getTimeOffRequests',
        unapprovedTimeOffs: 'timeOffRequest/getUnapprovedEmployeesTimeOff',
        numTimeOffs: 'timeOffRequest/getTimeOffCount'
      }),

      timeOffList(): ITimeOffRequest {
        return this.showRequestApprovalList
          ? this.unapprovedTimeOffs
          : this.timeOffRequests;
      }
    },

    watch: {
      params: {
        handler(value: ITimeOffRequestSearchParams) {
          this.debounceFetchTimeOffRequests({ ...value, page: 1 }).then(() => {
            this.page = 1;
          });
        },
        deep: true,
        immediate: true
      }
    },

    methods: {
      ...mapActions({
        fetchTimeOffRequests: 'timeOffRequest/fetchTimeOffRequests',
        deleteTimeOffRequest: 'timeOffRequest/deleteTimeOffRequest'
      }),

      moment,

      openActivitesDialog(request: ITimeOffRequest) {
        this.$refs.timeOffRequestActivityDialog.open(request);
      },

      clearAllErrors() {
        this.errorMessage = '';
        this.errorMessageDetail = '';
      },

      isUserOwner(request: ITimeOffRequestData): boolean {
        return (request.employee as IEmployee).id == this.currentUser.employee;
      },

      isUserApprover(request: ITimeOffRequestData): boolean {
        return request.approvers!.some(
          approver => (approver.approver as IUserData).id == this.currentUser.id
        );
      },

      canRejectOrApproveState(request: ITimeOffRequestData): boolean {
        return (
          this.isUserApprover(request) &&
          request.state == TimeOffStateEnum.Submitted
        );
      },

      // Owner of the personal request can cancel anytime when in submitted state.
      // Once approved, only manager can cancel
      // If user is approver or SA they can cancel when not rejected or cancelled
      canCancel(request: ITimeOffRequestData): boolean {
        let canCancel = false;

        if (this.isUserOwner(request)) {
          canCancel = request.state == TimeOffStateEnum.Submitted;
        } else if (
          request.state == TimeOffStateEnum.Approved &&
          (this.isUserApprover(request) || this.isUserHRPersonnel)
        ) {
          canCancel = true;
        }
        return canCancel;
      },

      close() {
        this.dialog = false;
      },

      openTimeOffDetailDialog(timeOff: ITimeOffRequest) {
        this.dialog = true;
        this.selectedTimeOff = new TimeOffRequest({
          ...timeOff,
          start: moment(timeOff.start)
            .utc(false)
            .format('YYYY-MM-DD HH:mm'),
          end: moment(timeOff.end)
            .utc(false)
            .format('YYYY-MM-DD HH:mm')
        });
      },

      genDaysOrHoursText(timeOff: ITimeOffRequest): string {
        let text = '';
        if (timeOff.timeoff_unit?.value == TimeOffUnitEnum.Days) {
          text = timeOff.amount_requested == 1 ? 'Day' : 'Days';
        } else {
          text = timeOff.amount_requested == 1 ? 'Hour' : 'Hours';
        }
        return text;
      },

      openFormDialog(timeOff: ITimeOffRequestData) {
        this.$emit('openFormDialog', timeOff);
      },

      async debounceFetchTimeOffRequests(params: ITimeOffRequestSearchParams) {
        this.clearAllErrors();
        this.loading = true;

        return this.debounce(this.genTimeOffRequests, 200)(params);
      },

      /**
       * @public api
       * To be used outside component as a reference
       */
      getCustomFilterParams(): ITimeOffRequestSearchParams {
        return { page: this.page };
      },

      genTimeOffRequests(params: IFetchTimeOffRequestPayload) {
        if (params.start) {
          params.start = moment(params.start)
            .startOf('day')
            .format('YYYY-MM-DDTHH:mm:ss');
        }
        if (params.end) {
          params.end = moment(params.end)
            .startOf('day')
            .format('YYYY-MM-DDTHH:mm:ss');
        }

        this.fetchTimeOffRequests(params)
          .catch(error => {
            if (error.response && error.response.data.detail) {
              this.errorMessageDetail = error.response.data.detail;
            }
            this.errorMessage = ErrorManager.extractApiError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      onUpdatePage(newPage: number) {
        if (newPage != this.page) {
          this.debounceFetchTimeOffRequests({
            ...this.params,
            page: newPage
          }).then(() => {
            this.page = newPage;
          });
        }
      },

      openCommentDialog(data: {
        request: ITimeOffRequest;
        state: TimeOffStateEnum;
      }) {
        this.dialog = false;
        this.$emit('openCommentDialog', data);
      }
    }
  });
